import { useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CtaButton } from "@/components/common/Cta";
import { Field, inputCls, Honeypot, isEmail, Success } from "./fields";
import { submitForm, errorMessage } from "@/lib/api";
import { CONTACT } from "@/data/site";

const SECTOR_OPTIONS = ["Housing scheme", "Apartments & duplexes", "Commercial", "Mixed-use", "Public sector", "Other"];
const EMPTY = { name: "", company: "", email: "", phone: "", project_name: "", location: "", units: "", sector: "", start_date: "", message: "", website: "" };

const TEXT_FIELDS = [
  ["company", "Company", "text"], ["phone", "Phone", "tel"],
  ["project_name", "Project name", "text"], ["location", "Project location", "text"],
  ["units", "Number of units", "text"], ["start_date", "Expected start date", "text"],
];

export const EnquiryForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.name.trim()) errs.name = "Please enter your name.";
    if (!isEmail(form.email)) errs.email = "Please enter a valid email.";
    if (!form.message.trim()) errs.message = "Please tell us about the project.";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setLoading(true);
    try {
      await submitForm("enquiries", { ...form, sector: form.sector || null });
      setDone(true);
      setForm(EMPTY);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <Success title="Thanks — enquiry received" testid="enquiry-success" onReset={() => setDone(false)}>
        <p>Your project details have been sent to our team. We'll be in touch using the email or phone number you gave us.</p>
        <p>Need to speak to someone sooner? Call Dublin on {CONTACT.phones[0].value}.</p>
      </Success>
    );
  }

  return (
    <form onSubmit={submit} noValidate className="grid gap-6 sm:grid-cols-2" data-testid="enquiry-form">
      <Honeypot value={form.website} onChange={set("website")} />
      <Field label="Name" htmlFor="enquiry-name" error={errors.name} required>
        <Input id="enquiry-name" value={form.name} onChange={set("name")} className={inputCls} data-testid="enquiry-name-input" />
      </Field>
      <Field label="Email" htmlFor="enquiry-email" error={errors.email} required>
        <Input id="enquiry-email" type="email" value={form.email} onChange={set("email")} className={inputCls} data-testid="enquiry-email-input" />
      </Field>
      {TEXT_FIELDS.map(([k, l, t]) => (
        <Field key={k} label={l} htmlFor={`enquiry-${k}`}>
          <Input id={`enquiry-${k}`} type={t} value={form[k]} onChange={set(k)} className={inputCls} data-testid={`enquiry-${k.replace("_", "-")}-input`}
            placeholder={k === "start_date" ? "e.g. Q2 2027" : undefined} />
        </Field>
      ))}
      <Field label="Sector" htmlFor="enquiry-sector" className="sm:col-span-2">
        <Select value={form.sector} onValueChange={(v) => setForm((f) => ({ ...f, sector: v }))}>
          <SelectTrigger id="enquiry-sector" className={inputCls} data-testid="enquiry-sector-select"><SelectValue placeholder="Select a sector" /></SelectTrigger>
          <SelectContent className="rounded-none border-canvas-line bg-canvas-800 text-ink" data-lenis-prevent>
            {SECTOR_OPTIONS.map((s) => <SelectItem key={s} value={s} data-testid={`enquiry-sector-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </Field>
      <Field label="Message" htmlFor="enquiry-message" error={errors.message} required className="sm:col-span-2">
        <Textarea id="enquiry-message" rows={6} value={form.message} onChange={set("message")} className={`${inputCls} h-auto min-h-[150px] py-3`} data-testid="enquiry-message-input"
          placeholder="Scope, programme, house types, heating strategy…" />
      </Field>
      <div className="flex flex-col gap-4 sm:col-span-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-steel">By sending this form you agree to our privacy notice.</p>
        <CtaButton type="submit" disabled={loading} testid="enquiry-submit-btn">{loading ? "Sending…" : "Send enquiry"}</CtaButton>
      </div>
    </form>
  );
};
